/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 드래그 클래스를 정의한다.
 *
 * @file /src/utils/Dragger.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 6. 22.
 */
import * as parent from '@base/Base';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 드래그가 시작될 때
     *
     * @param $target - 드래깅 타겟
     * @param dragger - 드래거
     */
    start?: ($target: Dom, dragger: Dragger) => void | Promise<void>;

    /**
     * 드래그 중일 때
     *
     * @param $target - 드래깅 타겟
     * @param dragger - 드래거
     */
    drag?: ($target: Dom, dragger: Dragger) => void | Promise<void>;

    /**
     * 드래그가 종료되었을 때
     *
     * @param $target - 드래깅 타겟
     * @param dragger - 드래거
     */
    end?: ($target: Dom, dragger: Dragger) => void | Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 포인터 타입
     */
    pointerType?: string[];

    /**
     * 커서모양
     */
    cursor?: string;

    /**
     * 드래그 시작까지의 대기 시간 (ms, 기본값 0)
     * 지정하면 pointerdown 후 해당 시간이 지나도 누르고 있을 때만 start 이벤트를 발생시킨다.
     */
    delay?: number;

    /**
     * 드래그를 허용할지 여부를 판단하는 함수
     */
    isActive?: () => boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export class Dragger extends parent.Base {
    /**
     * 활성화된 드래그
     */
    static activeId: number = null;

    /**
     * 모든 드래거 객체
     */
    static draggers: Map<number, Dragger> = new Map();

    /**
     * 포인트 이벤트 등록여부
     */
    static isListening: boolean = false;

    /**
     * 포인터 타입
     */
    pointerType: string[];

    /**
     * 커서모양
     */
    cursor: string;

    /**
     * 드래그 시작까지의 대기 시간 (ms)
     */
    startDelay: number = 0;

    /**
     * 대기 타이머
     */
    startTimer: ReturnType<typeof setTimeout> = null;

    /**
     * 드래그를 허용할지 여부를 판단하는 함수
     */
    isActive: () => boolean;

    /**
     * 대기 중 여부 (pointerdown 후 delay 경과 전)
     */
    pending: boolean = false;

    /**
     * 드래그 시작 여부 (delay 경과 후 start 이벤트 발생됨)
     */
    started: boolean = false;

    /**
     * 드래거 ID
     */
    draggerId: number = null;

    /**
     * 드래그 시작된 위치
     */
    firstPosition: { x: number; y: number } = { x: 0, y: 0 };

    /**
     * 드래그 끝난 위치
     */
    lastPosition: { x: number; y: number } = { x: 0, y: 0 };

    /**
     * 드래깅 타겟
     */
    $target: Dom;

    /**
     * 드래그 클래스를 정의한다.
     *
     * @param $target - 드래깅 타겟
     * @param properties - 초기 설정
     */
    constructor(
        $target: Dom,
        public properties: Properties<Dragger> = null
    ) {
        super(properties);

        this.$target = $target;
        this.pointerType = this.properties.pointerType ?? ['mouse', 'touch', 'pen'];
        this.startDelay = this.properties.delay ?? 0;
        this.cursor = this.properties.cursor ?? null;
        this.isActive = this.properties.isActive ?? null;

        this.$target?.on('pointerdown', (_, e: PointerEvent) => {
            if (e.button != 0) return;
            if (this.pointerType.includes(e.pointerType) == false) return;
            if (this.isActive?.() === false) return;

            this.draggerId = e.pointerId;
            this.firstPosition = { x: e.clientX, y: e.clientY };
            this.lastPosition = this.firstPosition;

            this.$target.getEl().setPointerCapture(e.pointerId);

            Dragger.listen();
            Dragger.draggers.set(e.pointerId, this);

            if (this.startDelay > 0) {
                this.pending = true;
                this.startTimer = setTimeout(async () => {
                    this.pending = false;
                    this.started = true;
                    Dragger.showOverlay(this.cursor);
                    await this.fireEvent('start', [this.$target, this]);
                    if (this.lastPosition.x !== this.firstPosition.x || this.lastPosition.y !== this.firstPosition.y) {
                        this.fireEvent('drag', [this.$target, this]);
                    }
                }, this.startDelay);
            } else {
                this.started = true;
                Dragger.showOverlay(this.cursor);
                this.fireEvent('start', [this.$target, this]);
            }
        });
    }

    /**
     * 현재 활성화된 드래거를 가져온다.
     *
     * @returns 활성화된 드래거
     */
    static getActivePointer(): Dragger {
        if (Dragger.activeId !== null && Dragger.draggers.has(Dragger.activeId) == true) {
            return Dragger.draggers.get(Dragger.activeId);
        }

        return null;
    }

    /**
     * 포인터가 드래그 하고 있는 DOM 객체를 가져온다.
     *
     * @return 드래깅 타겟
     */
    getTarget(): Dom {
        return this.$target;
    }

    /**
     * 포인터 상태를 업데이트한다.
     *
     * @param e - 포인터 이벤트
     */
    update(e: PointerEvent): void {
        this.lastPosition = { x: e.clientX, y: e.clientY };
        if (this.started == true) {
            this.fireEvent('drag', [this.$target, this]);
        }
    }

    /**
     * 포인터를 해제한다.
     */
    release(): void {
        if (this.pending == true) {
            clearTimeout(this.startTimer);
            this.startTimer = null;
            this.pending = false;
        }
        if (this.draggerId !== null && this.$target.getEl().hasPointerCapture(this.draggerId) == true) {
            this.$target.getEl().releasePointerCapture(this.draggerId);
        }
        Dragger.release();
        Dragger.draggers.delete(this.draggerId);
        if (this.started == true) {
            this.fireEvent('end', [this.$target, this]);
        }
        this.started = false;
        this.draggerId = null;
        Dragger.hideOverlay();
    }

    /**
     * 드래그 시작된 위치를 가져온다.
     *
     * @returns 드래그 시작 위치
     */
    getStart(): { x: number; y: number } {
        return this.firstPosition;
    }

    /**
     * 마우스의 절대 위치를 가져온다.
     *
     * @returns 마우스의 절대 위치
     */
    getPosition(): { x: number; y: number } {
        return this.lastPosition;
    }

    /**
     * 드래그 이동 거리를 가져온다.
     *
     * @returns 드래그 이동 거리
     */
    getLength(): { x: number; y: number } {
        return {
            x: this.lastPosition.x - this.firstPosition.x,
            y: this.lastPosition.y - this.firstPosition.y,
        };
    }

    /**
     * 드래그를 제거한다.
     */
    async remove(): Promise<void> {}

    /**
     * 포인터 이동 이벤트를 처리한다.
     *
     * @param e - 포인터 이벤트
     */
    static pointermove(e: PointerEvent): void {
        Dragger.activeId = e.pointerId;
        Dragger.draggers.get(e.pointerId)?.update(e);
    }

    /**
     * 포인터 해제 이벤트를 처리한다.
     *
     * @param e - 포인터 이벤트
     */
    static pointerup(e: PointerEvent): void {
        if (Dragger.activeId === e.pointerId) {
            Dragger.activeId = null;
        }
        Dragger.draggers.get(e.pointerId)?.release();
    }

    /**
     * 포인터 취소 이벤트를 처리한다.
     *
     * @param e - 포인터 이벤트
     */
    static pointercancel(e: PointerEvent): void {
        if (Dragger.activeId === e.pointerId) {
            Dragger.activeId = null;
        }
        Dragger.draggers.get(e.pointerId)?.release();
    }

    /**
     * 드래그 중 커서를 고정하기 위한 전체화면 overlay 를 표시한다.
     *
     * @param cursor - 커서 모양
     */
    static showOverlay(cursor: string): void {
        let $overlay = Html.get('[data-role="dragger-overlay"]');
        if ($overlay === null) {
            $overlay = Html.create('div', { 'data-role': 'dragger-overlay' });
            $overlay.setStyle('position', 'fixed');
            $overlay.setStyle('inset', '0');
            $overlay.setStyle('z-index', '99999');
            Html.get('body').append($overlay);
        }
        if (cursor !== null) {
            Html.get('body').setStyle('cursor', cursor, true);
        }
    }

    /**
     * 드래그 중 커서를 고정하기 위한 전체화면 overlay 를 제거한다.
     */
    static hideOverlay(): void {
        Html.get('[data-role="dragger-overlay"]')?.remove();
        Html.get('body').setStyle('cursor', null);
    }

    /**
     * 포인터 이벤트를 등록한다.
     */
    static listen(): void {
        if (Dragger.isListening == true) {
            return;
        }
        Dragger.isListening = true;
        Html.on('pointermove', Dragger.pointermove, { passive: true });
        Html.on('pointerup', Dragger.pointerup, { passive: true });
        Html.on('pointercancel', Dragger.pointercancel, { passive: true });
    }

    /**
     * 포인터 이벤트를 해제한다.
     */
    static release(): void {
        Html.off('pointermove', Dragger.pointermove);
        Html.off('pointerup', Dragger.pointerup);
        Html.off('pointercancel', Dragger.pointercancel);
        Dragger.isListening = false;
    }
}
